import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';
import { EMPTY, Observable, Subject, takeUntil } from 'rxjs';
import {
  ButtonComponent,
  CurrencyPipe,
  HoldingResponse,
  IfFeatureService,
  HrefDirective,
  ListComponent,
  ListItemComponent,
  ListItemLabelComponent,
  ListItemValueComponent,
  ModalRef,
  ModalService,
  OverlayAsyncOpenEvent,
  OverlayComponent,
  OverlayHeaderComponent,
  OverlayService,
  OverlayToggleDirective,
  PercentPipe,
  SymbolLogoComponent,
  TickerSymbolComponent,
  TickerSymbolTableContainerComponent,
  DataAnalyticsService
} from '@tdwb/core';
import { FplRecallSecurityComponent } from '../recall-security/fpl-recall-security.component';
import { FplRestService } from '../services/fpl-rest.service';
import { StockLoanDetails } from '../fpl.domain';

@Component({
  selector: 'td-wb-fpl-on-loan',
  templateUrl: 'fpl-on-loan.component.html',
  styleUrls: ['fpl-on-loan.component.scss'],
  imports: [
    OverlayToggleDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    SymbolLogoComponent,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    ButtonComponent,
    HrefDirective,
    FplRecallSecurityComponent,
    TickerSymbolComponent,
    TickerSymbolTableContainerComponent,
    TranslateDirective,
    CurrencyPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FplOnLoanComponent implements OnChanges, OnDestroy {
  private static readonly DEFAULT_EMPTY_PLACEHOLDER: string = '-';
  private fplRecallSecurityModalRef: ModalRef<unknown>;
  private asyncOpenEvent: OverlayAsyncOpenEvent;
  private readonly destroy: Subject<void> = new Subject<void>();
  isRecallActionPending: boolean;
  loanQuantityDisplayValue: string = FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER;
  initialInterestRateDisplayValue: string = FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER;
  currentInterestRateDisplayValue: string = FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER;
  showLogo: boolean = false;

  @Input() accountId: string;
  @Input() holding: HoldingResponse;
  @ViewChild('fplRecallSecurityModalTemplate') fplRecallSecurityModalContent: TemplateRef<FplRecallSecurityComponent>;

  constructor(private modalService: ModalService,
              private overlayService: OverlayService,
              private fplRestService: FplRestService,
              private percentPipe: PercentPipe,
              private changeDetectorRef: ChangeDetectorRef,
              private ifFeatureService: IfFeatureService,
              private dataAnalyticsService: DataAnalyticsService) {
    this.showLogo = this.ifFeatureService.getFeatureFlag('wbSymbolLogoOnHoldingsEnabled');
  }

  ngOnChanges(): void {
    if (this.holding.quantity > 0) {
      const loanQuantity = this.holding.onLoanQuantity ?? 0;
      this.loanQuantityDisplayValue = `${loanQuantity}/${this.holding.quantity} (${this.getFormattedInterestRate((loanQuantity / this.holding.quantity) * 100)})`;
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  asyncOpen(event: OverlayAsyncOpenEvent): void {
    this.asyncOpenEvent = event;
  }

  openOnLoanOverlay(): void {
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:on loan');
    if (this.holding.cusip && this.accountId) {
      this.fplRestService.getStockLoanDetails(this.holding.cusip, this.accountId)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (stockLoanDetails: StockLoanDetails): void => {
            if (stockLoanDetails !== undefined) {
              this.isRecallActionPending = stockLoanDetails.pendingRecall === true;
              this.populateInterestRates(stockLoanDetails);
              this.openAsyncOnLoanOverlay();
              this.changeDetectorRef.markForCheck();
            }
          },
          error: (): Observable<never> => EMPTY
        });
    }
  }

  private openAsyncOnLoanOverlay(): void {
    if (this.asyncOpenEvent !== undefined) {
      this.asyncOpenEvent.open();
    }
  }

  private populateInterestRates(stockLoanDetails: StockLoanDetails): void {
    // Initial Interest Rate
    if (stockLoanDetails.initialRateMin !== undefined && stockLoanDetails.initialRateMax !== undefined) {
      this.initialInterestRateDisplayValue = `${this.getFormattedInterestRate(stockLoanDetails.initialRateMin)} - ${this.getFormattedInterestRate(stockLoanDetails.initialRateMax)}`;
    } else if (stockLoanDetails.initialRateMin !== undefined) {
      this.initialInterestRateDisplayValue = this.getFormattedInterestRate(stockLoanDetails.initialRateMin);
    } else if (stockLoanDetails.initialRateMax !== undefined) {
      this.initialInterestRateDisplayValue = this.getFormattedInterestRate(stockLoanDetails.initialRateMax);
    }

    // Curent Interest Rate
    if (stockLoanDetails.currentRateMin !== undefined && stockLoanDetails.currentRateMax !== undefined) {
      this.currentInterestRateDisplayValue = `${this.getFormattedInterestRate(stockLoanDetails.currentRateMin)} - ${this.getFormattedInterestRate(stockLoanDetails.currentRateMax)}`;
    } else if (stockLoanDetails.currentRateMin !== undefined) {
      this.currentInterestRateDisplayValue = this.getFormattedInterestRate(stockLoanDetails.currentRateMin);
    } else if (stockLoanDetails.currentRateMax !== undefined) {
      this.currentInterestRateDisplayValue = this.getFormattedInterestRate(stockLoanDetails.currentRateMax);
    }
  }

  private getFormattedInterestRate(interestRate: number): string {
    return this.percentPipe.transform(interestRate, { format: 'n2', emptyPlaceholder: FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER });
  }

  openFplRecallSecurityModal(): void {
    this.overlayService.close();
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:recall');
    this.fplRecallSecurityModalRef = this.modalService.open(this.fplRecallSecurityModalContent, {
      size: 'extra-small'
    });

    this.fplRecallSecurityModalRef.afterClose().subscribe((result: unknown): void => {
      if (result !== undefined) {
        this.openAsyncOnLoanOverlay();
      }
    });
  }

  closeFplRecallSecurityModal(): void {
    this.fplRecallSecurityModalRef?.close();
  }

  closeOnLoanOverlay(): void {
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:close');
  }
}
